<template>
	<div class="flex h-full max-h-full flex-col gap-4">
		<n-spin
			class="-mr-4 flex w-full grow flex-col overflow-hidden"
			content-class="flex grow flex-col overflow-hidden"
			:show="loading"
		>
			<template v-if="list.length">
				<n-scrollbar v-if="useScroller" content-class="flex flex-col gap-4" trigger="none" class="grow pr-4">
					<PatientCard v-for="patient in list" :key="patient.id" :patient :hide-status="hidePatientStatus" />
				</n-scrollbar>
				<div v-else class="flex flex-col gap-4">
					<PatientCard v-for="patient in list" :key="patient.id" :patient :hide-status="hidePatientStatus" />
				</div>
			</template>

			<div v-else class="flex h-full w-full flex-col items-center justify-center py-20" :class="emptyClass">
				<slot name="empty">
					<EmptyList
						v-if="!loading"
						:title="
							hasFilters
								? $t(`patients.empty_state.no_patients_with_filters.title`)
								: $t(`patients.empty_state.no_patients.title`)
						"
						:subtitle="
							hasFilters
								? $t(`patients.empty_state.no_patients_with_filters.subtitle`)
								: $t(`patients.empty_state.no_patients.subtitle`)
						"
					/>
				</slot>
			</div>
		</n-spin>
		<div v-if="totalItems && usePagination" class="flex flex-wrap justify-between gap-6">
			<div v-html="$t(`patients.common.list_total`, { count: totalItems })" />
			<n-pagination v-model:page="page" :page-size :page-slot="5" :item-count="totalItems" />
		</div>
	</div>
</template>

<script setup lang="ts">
import type { PatientsListSortKey } from "@/api/endpoints/patients"
import type { SortDirection } from "@/types/common"
import type { Patient, PatientStatus } from "@/types/patient"
import { watchDebounced } from "@vueuse/core"
import axios from "axios"
import { NPagination, NScrollbar, NSpin, useMessage } from "naive-ui"
import { computed, ref, toRefs } from "vue"
import Api from "@/api"
import EmptyList from "@/components/common/empty/List.vue"
import { useSyncUrlParams } from "@/composables/router/useSyncUrlParams"
import { getApiErrorMessage } from "@/utils"
import PatientCard from "../PatientCard.vue"

const props = withDefaults(
	defineProps<{
		filters?: {
			text?: string | null
			status?: PatientStatus | null
			deliveringIn?: number | null
		}
		sort?: {
			key?: PatientsListSortKey | null
			direction?: SortDirection | null
		}
		useScroller?: boolean
		usePagination?: boolean
		pageSize?: number
		emptyClass?: string
		hidePatientStatus?: boolean
	}>(),
	{
		usePagination: true,
		pageSize: 20
	}
)

const emit = defineEmits<{
	(e: "list", value: Patient[]): void
	(e: "total-count", value: number): void
}>()

const { filters, sort, useScroller, usePagination, pageSize, emptyClass, hidePatientStatus } = toRefs(props)

const message = useMessage()
const loading = ref(false)
const list = ref<Patient[]>([])
const totalItems = ref<number>(0)
const page = ref(1)

const hasFilters = computed(() => !!filters.value?.text || !!filters.value?.status || !!filters.value?.deliveringIn)

let listAbortController: AbortController | null = null

function getList() {
	loading.value = true

	listAbortController?.abort()
	listAbortController = new AbortController()

	Api.patients
		.list(
			{
				perPage: pageSize.value,
				page: page.value,
				filters: {
					text: filters.value?.text || undefined,
					status: filters.value?.status || undefined,
					deliveringIn: filters.value?.deliveringIn || undefined
				},
				sort: { key: sort.value?.key || undefined, direction: sort.value?.direction || undefined }
			},
			listAbortController.signal
		)
		.then(res => {
			list.value = res.data.items
			totalItems.value = res.data.total_items
			loading.value = false
			emit("list", list.value)
			emit("total-count", totalItems.value)
		})
		.catch(err => {
			if (!axios.isCancel(err)) {
				loading.value = false
				message.error(getApiErrorMessage(err))
			}
		})
}

watchDebounced(
	[() => filters.value?.text, () => filters.value?.status, () => sort.value?.key, () => sort.value?.direction, page],
	() => {
		getList()
	},
	{ debounce: 300, maxWait: 5000, immediate: true }
)

watchDebounced(
	[() => filters.value?.text, () => filters.value?.status, () => filters.value?.deliveringIn],
	val => {
		if (val[0] || val[1] || val[2]) {
			page.value = 1
		}
	},
	{ debounce: 200, maxWait: 5000 }
)

useSyncUrlParams("patients_list_page", page)
</script>
